import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { AlertCircle, ChevronLeft, ChevronRight, Download, X } from '../../icons';
import { getDailyWork, type DailyWorkDto } from '../../api/attendance';
// 월별 근무 그림 — 자료가 여기 있으므로 여기 붙인다(2026-09-26).
import { MonthlyWorkChart } from './AttendanceCharts';
import { DAILY_STATUS_LABEL, formatWorkMinutes } from '../../lib/daily-work';
// 근무 상태내역(2026-09-06 · 판 C) — 낱말·배지는 **상태바·레일과 같은 것**을 쓴다.
import { fallbackLogFrom, toWorkLogLines } from '../../lib/work-status-log';
import { tLayout } from '../../lib/layout-i18n';
import {
  WEEK_BASE_MINUTES, WEEK_CAP_MINUTES, daySegments, fromKey,
  monthsOf, periodKeys, periodTotals, spanText, toKey,
  type DaySeg, type PeriodRange,
} from '../../lib/work-period';
import './MyWorkPeriod.css';

/**
 * 내 근태 상세보기 — **주간이 기본, 월간으로 넓힌다** (2026-09-06 · 판 B · 정본 P3-5).
 *
 * ## 무엇을 담나
 * 요약 한 섹션(세 묶음) → 캘린더(주 일곱 칸 · 달 격자) → 고른 날.
 * 고른 날은 **주간이면 바로 아래, 월간이면 팝업**이다 — 격자가 몇 줄이냐가 자리를 정한다.
 *
 * ## ★★★ 셈은 화면이 하지 않는다
 * 요약 막대 · 달력 칸 · 하루 띠 셋이 같은 값을 읽는다. 셋이 제 손으로 세면 같은 하루가
 * 자리마다 달라진다 — 규칙은 `lib/work-period.ts` 한 곳에 있다.
 *
 * ## ★★ 없는 것은 그리지 않는다
 * **휴게 자리**는 근무제에 「몇 시부터」가 없어 띠에 안 그린다 — 대신 **왜 없는지**를 적는다.
 *
 * ## ★★★ 근무 상태내역 (2026-09-06 · 판 C)
 * 오른쪽 칸은 종전에 **출근·퇴근 두 줄**뿐이었고, 화면이 스스로 「근무 상태 변경은 아직
 * 서버에 남지 않습니다」라고 적고 있었다. 이제 서버 이력(`hr_work_status_logs`)이 생겨
 * 회의중·자리비움·외근·출장·**퇴근 취소**가 함께 선다 — **그래서 그 문장을 걷어냈다**
 * (기능이 바뀌면 화면이 적어 둔 안내도 함께 고쳐야 한다. 안 고치면 거짓말이 된다).
 */

const WEEKDAY = ['일', '월', '화', '수', '목', '금', '토'];
const RANGES: Array<{ value: PeriodRange; label: string }> = [
  { value: 'week', label: '주간' },
  { value: 'month', label: '월간' },
];
/** 24시간을 백분율로 — 띠와 눈금이 **같은 자를 쓴다**. */
const pct = (min: number) => `${(min / (24 * 60)) * 100}%`;

const SEG_LABEL: Record<DaySeg['kind'], string> = {
  work: '업무시간', ot: '승인초과근로', un: '업무미포함시간', night: '야간근로', leave: '휴가',
};

export interface MyWorkPeriodProps {
  /** 그날에 걸린 신청 — 「낸 신청」에서 온다(이 화면이 다시 부르지 않는다). */
  requestsByDate?: ReadonlyMap<string, Array<{ label: string; status: string }>>;
  /** 화면 머리의 「신청이력」이 가리키는 자리. */
  onGoRequests?: () => void;
  /**
   * 마감 — **표시는 달력 칸이, 조치는 하루 상세가** 한다 (2026-09-08 사용자 지시).
   *
   * ★★★ 처음엔 달력 «아래» 별도 상자(`openDays` 목록)로 붙였는데, 그러면 **같은 날이 두
   *    자리에 적힌다** — 달력이 이미 그 날을 그리고 있다. 그래서 **목록을 받지 않는다**:
   *    어느 날이 이상인지는 이 컴포넌트가 이미 가진 `needsClose` 로 안다.
   * ★ 주간·월간이 **같은 칸(`DayCell`)**을 쓰므로 한 번 고치면 둘 다 선다.
   */
  /** 「마감」을 누르면 부른다. 없으면 단추를 안 그린다. */
  onCloseDay?: (date: string) => void;
  /** 지금 마감 중인 날 — 그 줄만 잠근다. */
  closingDate?: string | null;
  /** 마감이 실패했을 때의 말. */
  closeError?: string | null;
  /**
   * 값이 바뀌면 **캐시를 버리고 다시 부른다**.
   * ★★ 달마다 받은 값을 캐시하므로, 마감한 뒤 이 신호가 없으면 그 칸이 「마감 이상」인 채
   *    남는다 — 사람은 눌렀는데 화면이 안 바뀐 것으로 읽는다.
   */
  reloadKey?: number;
}

export function MyWorkPeriod({
  requestsByDate, onGoRequests, onCloseDay, closingDate = null, closeError = null,
  reloadKey = 0,
}: MyWorkPeriodProps): JSX.Element {
  const [params, setParams] = useSearchParams();
  const raw = params.get('range');
  const range: PeriodRange = raw === 'month' ? 'month' : 'week';
  const [anchor, setAnchor] = useState(() => new Date());
  const [picked, setPicked] = useState<string | null>(null);
  const [popOpen, setPopOpen] = useState(false);
  const [state, setState] = useState<'loading' | 'ok' | 'failed'>('loading');
  const [byMonth, setByMonth] = useState<Map<string, DailyWorkDto[]>>(new Map());
  /**
   * 그 달의 **쉬는 날** — 서버가 준다 (2026-09-11 · A-3).
   *
   * ★ `null` 이면 **옛 서버**라 그 칸이 없다 — 그때는 종전처럼 토·일로 본다(회귀 0).
   *   빈 Set 과 `null` 은 다른 말이다: 빈 Set 은 「쉬는 날이 하나도 없다」이다.
   */
  const [restSet, setRestSet] = useState<ReadonlySet<string> | null>(null);
  const restCache = useRef(new Map<string, string[] | null>());
  const cache = useRef(new Map<string, DailyWorkDto[]>());
  /** 마지막으로 본 새로고침 신호 — 바뀐 것을 **효과 안에서** 알아채야 캐시를 버릴 수 있다. */
  const seenReload = useRef(reloadKey);

  const keys = useMemo(() => periodKeys(range, anchor), [range, anchor]);
  const months = useMemo(() => monthsOf(keys), [keys]);

  // ★ 주가 달을 넘으면 **두 달**을 부른다 — 서버 집계가 달 단위라 한쪽만 불러선 주가 안 찬다.
  useEffect(() => {
    let alive = true;
    // ★ 마감처럼 «값이 바뀌는 일»이 있었으면 캐시를 버린다 — 안 그러면 옛 값을 그대로 그린다.
    if (seenReload.current !== reloadKey) {
      cache.current.clear(); restCache.current.clear(); seenReload.current = reloadKey;
    }
    const want = months.map((m) => `${m.year}-${m.month}`);
    if (want.every((k) => cache.current.has(k))) {
      setByMonth(new Map(cache.current));
      setRestSet(mergeRest(restCache.current, want));
      setState('ok'); return;
    }
    setState('loading');
    void (async () => {
      try {
        await Promise.all(months.map(async (m) => {
          const k = `${m.year}-${m.month}`;
          if (cache.current.has(k)) return;
          // ★ 승인 연장의 **시각**도 이 응답이 준다(판 B) — 임금 API 를 또 부르지 않는다.
          const d = await getDailyWork(m.year, m.month);
          cache.current.set(k, d.days ?? []);
          // ★ 옛 서버면 `undefined` — `null` 로 담아 「모른다」를 「없다」와 가른다.
          restCache.current.set(k, d.restDates ?? null);
        }));
        if (!alive) return;
        setByMonth(new Map(cache.current));
        setRestSet(mergeRest(restCache.current, want));
        setState('ok');
      } catch {
        if (alive) setState('failed');
      }
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [months, reloadKey]);

  const byDate = useMemo(() => {
    const m = new Map<string, DailyWorkDto>();
    for (const v of byMonth.values()) for (const d of v) m.set(d.date, d);
    return m;
  }, [byMonth]);

  /**
   * 그림에 넘길 그 달의 날들 — **격자가 그리는 날과 «같은 것»**(`keys`)에서 뽑는다.
   * ★ `byMonth` 를 통째로 넘기면 **앞뒤 달까지** 섞여 들어간다(캐시가 여러 달을 들고 있다).
   * ★ 기록이 없는 날은 빼고 넘긴다 — 차트는 `days.length === 0` 이면 아무것도 안 그린다.
   */
  const monthDays = useMemo(
    () => keys.map((k) => byDate.get(k)).filter((d): d is DailyWorkDto => d !== undefined),
    [keys, byDate],
  );

  const todayKey = toKey(new Date());
  const totals = useMemo(() => periodTotals(keys, byDate, todayKey), [keys, byDate, todayKey]);
  const cap = range === 'week' ? WEEK_CAP_MINUTES : WEEK_CAP_MINUTES * 4;
  const base = range === 'week' ? WEEK_BASE_MINUTES : WEEK_BASE_MINUTES * 4;

  // ★ 기간이 바뀌면 고른 날을 버린다 — 지난 주 날짜가 골라진 채 남으면 안 된다.
  useEffect(() => {
    setPicked((p) => (p && keys.includes(p) ? p : null));
    setPopOpen(false);
  }, [keys]);
  /**
   * 처음부터 **하나를 고른 채로** 연다 — 빈 껍데기는 무엇을 하는 화면인지 못 말한다.
   * ★ 오늘에 기록이 있으면 오늘, 없으면 **기록이 있는 마지막 날**. 둘 다 없으면 안 고른다
   *   (지어낸 날을 펼치면 「그날 아무것도 없다」가 화면의 첫인상이 된다).
   */
  useEffect(() => {
    if (picked !== null) return;
    if (keys.includes(todayKey) && byDate.has(todayKey)) { setPicked(todayKey); return; }
    const last = [...keys].reverse().find((k) => byDate.has(k));
    if (last) setPicked(last);
  }, [picked, keys, todayKey, byDate]);

  const pickRange = useCallback((next: PeriodRange) => {
    setParams((prev) => {
      const p = new URLSearchParams(prev);
      p.set('range', next);
      return p;
    }, { replace: true });
  }, [setParams]);

  const shift = useCallback((dir: -1 | 1) => {
    setAnchor((a) => (range === 'week'
      ? new Date(a.getTime() + dir * 7 * 86_400_000)
      : new Date(Date.UTC(a.getUTCFullYear(), a.getUTCMonth() + dir, 1))));
  }, [range]);

  const pickDay = useCallback((key: string) => {
    setPicked(key);
    if (range === 'month') setPopOpen(true);
  }, [range]);

  /**
   * 목록 내려받기 — **CSV** 다(엑셀에서 그대로 열린다).
   * ★ 화면에서 건 것이 아니라 **지금 기간의 전부**가 담긴다 — 화면이 그 사실을 적는다.
   */
  const download = useCallback(() => {
    const head = ['날짜', '요일', '상태', '출근', '퇴근', '인정 근로(분)', '승인 연장(분)', '미신청(분)', '휴게(분)'];
    const rows = keys.map((k) => {
      const d = byDate.get(k);
      const w = WEEKDAY[fromKey(k).getUTCDay()];
      if (!d) return [k, w, '', '', '', '', '', '', ''];
      return [k, w, DAILY_STATUS_LABEL[d.status], d.checkIn ?? '', d.checkOut ?? '',
        String(d.creditedMinutes), String(d.overtimeMinutes), String(d.unclaimedMinutes), String(d.breakMinutes)];
    });
    const csv = [head, ...rows].map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\r\n');
    // ★ BOM 을 붙인다 — 없으면 엑셀이 한글을 깨뜨린다.
    const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `근무내역_${keys[0]}_${keys[keys.length - 1]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }, [keys, byDate]);

  const label = range === 'week'
    ? `${keys[0]?.slice(5).replace('-', '/')} ~ ${keys[keys.length - 1]?.slice(5).replace('-', '/')}`
    : `${anchor.getUTCFullYear()}년 ${anchor.getUTCMonth() + 1}월`;

  const pickedDay = picked ? byDate.get(picked) ?? null : null;

  return (
    <section className="wkp" aria-label="내 근태 상세보기">
      <div className="wkp__head">
        <div className="wkp__nav">
          <button type="button" className="wkp__navbtn" onClick={() => shift(-1)} aria-label="이전">
            <ChevronLeft size={15} aria-hidden />
          </button>
          <b className="wkp__label">{label}</b>
          <button type="button" className="wkp__navbtn" onClick={() => shift(1)} aria-label="다음">
            <ChevronRight size={15} aria-hidden />
          </button>
        </div>
        <div className="wkp__tools">
          <div className="wkp__pills" role="tablist" aria-label="기간">
            {RANGES.map((r) => (
              <button
                key={r.value}
                type="button"
                role="tab"
                aria-selected={range === r.value}
                className={`wkp__pill${range === r.value ? ' is-on' : ''}`}
                onClick={() => pickRange(r.value)}
              >
                {r.label}
              </button>
            ))}
          </div>
          {onGoRequests && (
            <button type="button" className="wkp__btn" onClick={onGoRequests}>신청이력</button>
          )}
          <button type="button" className="wkp__btn" onClick={download}>
            <Download size={14} aria-hidden />목록 다운로드
          </button>
        </div>
      </div>

      {/* ── 요약 — 한 섹션 · 세 묶음 ── */}
      <div className="wkp__sum">
        <div className="wkp__g">
          <span className="wkp__gt">{range === 'week' ? '주간 누적' : '월간 누적'}</span>
          <span className="wkp__big">
            <b>{spanText(totals.totalMinutes)}</b>
            <span className="wkp__of">
              업무 {spanText(totals.creditedMinutes)} · 승인 초과 {spanText(totals.overtimeMinutes)}
            </span>
          </span>
          <div className="wkp__barwrap">
            <div
              className="wkp__bar"
              role="img"
              aria-label={`누적 ${spanText(totals.totalMinutes)}, 법정 상한 ${spanText(cap)} 중`}
            >
              <span className="wkp__fill" style={{ width: `${Math.min(100, (totals.totalMinutes / cap) * 100)}%` }} />
              <span className="wkp__tick" style={{ left: `${(base / cap) * 100}%` }} />
            </div>
            <span className="wkp__zero">0</span>
            <span className="wkp__mark" style={{ left: `${(base / cap) * 100}%` }}>소정 {spanText(base)}</span>
            {/* ★★★ 52:00 은 «계약» 이 아니라 **법정 상한**이다(소정 40 + 연장 12 · §53). */}
            <span className="wkp__end"><b>{spanText(cap)}</b> 법정 상한</span>
          </div>
        </div>

        <span className="wkp__rule" aria-hidden />

        <div className="wkp__g">
          <span className="wkp__gt">남은 것</span>
          <span className="wkp__row"><span>잔여 근무일</span><b>{totals.remainingWorkDays}일</b></span>
          <span className="wkp__row">
            <span>잔여 근로시간</span><b>{spanText(Math.max(0, cap - totals.totalMinutes))}</b>
          </span>
        </div>

        <span className="wkp__rule" aria-hidden />

        <div className="wkp__g">
          <span className="wkp__gt">{range === 'week' ? '이번 주' : '이번 달'}</span>
          <span className="wkp__row">
            <span>총 근로시간</span><b>{spanText(totals.totalMinutes)}</b>
          </span>
          <span className="wkp__row"><span>휴가</span><b>{totals.leaveDays}일</b></span>
        </div>
      </div>

      {state === 'loading' && <p className="wkp__note">불러오는 중…</p>}
      {state === 'failed' && <p className="wkp__bad" role="alert">근무 내역을 불러오지 못했습니다.</p>}

      {state === 'ok' && (range === 'week' ? (
        <div className="wkp__week" role="group" aria-label={label}>
          {keys.map((k) => <DayCell key={k} k={k} day={byDate.get(k) ?? null} picked={picked === k} onPick={pickDay} todayKey={todayKey} restSet={restSet} />)}
        </div>
      ) : (
        <MonthGrid keys={keys} byDate={byDate} picked={picked} onPick={pickDay} todayKey={todayKey} restSet={restSet} />
      ))}

      {/*
        ★ 월별 근무 그림 — **격자 «아래»** (2026-09-26 사용자 결정으로 붙였다 · 자리는 ㉯).
          · 덧붙이기만 한다 — 위에 넣으면 이 화면에 **표를 보러 온 사람**의 것이 아래로 밀린다.
          · 형제(`PremiumOverlapChart`)도 같은 꼴로 아래에 붙어 있다 — 두 화면의 자리가 같아진다.
          · **기본 접힘으로 두지 않았다**: 한 번 더 눌러야 보이면 이 화면에 온 사람이 바로 못 본다.
            화면이 길어지는 것이 문제가 되면 그때 접는다.
          · 월간에만 그린다 — 「월별」 그림이고, 주간 격자에는 이레치뿐이라 막대가 설 자리가 없다.
          · 자료는 **이미 받아 둔 것**이다(`byDate`) — 새 API 도, 새 요청도 없다.
        ⚠ 기준선은 `MonthlyWorkChart` 의 기본값(480분)이다. 소정이 다른 사람(단시간 근로자)의
          값은 이 화면에 없다 — 사람별 소정을 실어 주면 그때 넘긴다(후속 서버 요청).
      */}
      {state === 'ok' && range === 'month' && monthDays.length > 0 && (
        <MonthlyWorkChart days={monthDays} />
      )}

      {/* ★ 주간은 **바로 아래**, 월간은 **팝업** — 격자가 몇 줄이냐가 자리를 정한다. */}
      {state === 'ok' && range === 'week' && picked && (
        <DayDetail
          day={pickedDay}
          requests={requestsByDate?.get(picked)}
          requestsKnown={requestsByDate !== undefined}
          onCloseDay={onCloseDay}
          closingDate={closingDate}
          closeError={closeError}
        />
      )}
      {state === 'ok' && range === 'month' && popOpen && picked && (
        <div className="wkp__scrim" onMouseDown={() => setPopOpen(false)}>
          <div className="wkp__pop" role="dialog" aria-modal="true" aria-label={`${picked} 근무 내역`} onMouseDown={(e) => e.stopPropagation()}>
            <div className="wkp__pophd">
              <b>{dayTitle(picked)}</b>
              <button type="button" className="wkp__popx" onClick={() => setPopOpen(false)} aria-label="닫기">
                <X size={16} aria-hidden />
              </button>
            </div>
            <DayDetail
              day={pickedDay}
              requests={requestsByDate?.get(picked)}
              requestsKnown={requestsByDate !== undefined}
              inPop
              onCloseDay={onCloseDay}
              closingDate={closingDate}
              closeError={closeError}
            />
          </div>
        </div>
      )}
    </section>
  );
}

function dayTitle(key: string): string {
  const d = fromKey(key);
  return `${d.getUTCMonth() + 1}월 ${d.getUTCDate()}일 (${WEEKDAY[d.getUTCDay()]})`;
}

/**
 * 캘린더 칸 — **큰 숫자(총 근로) → 출 · 퇴 → 초과 알약**.
 * ★★ 일하지 않은 날은 숫자 대신 **낱말**이다 — 「0:00」으로 그리면 「조금 일했다」로 읽힌다.
 */
/**
 * 보고 있는 달들의 **쉬는 날을 합친다** (2026-09-11 · A-3).
 *
 * ★★★ **한 달이라도 «모른다»(옛 서버) 면 통째로 `null`** 이다. 아는 달만 합쳐 놓으면
 *    모르는 달의 날들이 전부 「기록 없음」으로 적혀 **거짓이 절반만 섞인다** — 그럴 바엔
 *    종전 규칙(토·일)으로 한 화면을 일관되게 두는 편이 낫다.
 */
function mergeRest(
  cache: ReadonlyMap<string, string[] | null>,
  want: readonly string[],
): ReadonlySet<string> | null {
  const out = new Set<string>();
  for (const k of want) {
    const v = cache.get(k);
    if (v == null) return null;
    for (const d of v) out.add(d);
  }
  return out;
}

function DayCell({ k, day, picked, onPick, todayKey, restSet }: {
  k: string; day: DailyWorkDto | null; picked: boolean; onPick: (k: string) => void; todayKey: string;
  /** 그 달의 쉬는 날 — **서버가 준다**. `null` 이면 옛 서버라 종전처럼 토·일로 본다. */
  restSet: ReadonlySet<string> | null;
}) {
  const d = fromKey(k);
  const dow = d.getUTCDay();
  const total = day ? day.creditedMinutes + day.overtimeMinutes : 0;
  /**
   * ★★★ **「휴일」인지는 «그 사람의 근무요일»이 정한다** (2026-09-11 · A-3).
   *
   *    종전에는 `dow === 0 || dow === 6` 이었다 — **주 6일 회사의 토요일이 「휴일」로** 적혔고,
   *    화~토 근무자의 월요일은 「기록 없음」으로 적혔다. 둘 다 거짓이다.
   * ★ 옛 서버(칸 없음)면 종전 그대로 토·일로 본다 — 회귀 0.
   * ⚠ 아래 `is-sun`·`is-sat` 는 **달력 색칠**(요일 관례)이라 그대로 둔다 — 그것은 근무일
   *   판정이 아니다. 한국 달력은 근무 여부와 무관하게 토·일에 색을 준다.
   */
  const isRest = restSet ? restSet.has(k) : (dow === 0 || dow === 6);
  const word = !day ? (k > todayKey ? '예정' : isRest ? '휴일' : '기록 없음')
    : day.status === 'LEAVE' ? '휴가' : total === 0 ? DAILY_STATUS_LABEL[day.status] : null;
  /**
   * ★★★ **마감 이상은 «달력 칸 안»에 선다** (2026-09-08 사용자 지시).
   *    처음엔 달력 «아래» 별도 상자로 붙였는데, 그러면 **같은 날이 두 자리에 적힌다** —
   *    달력이 이미 그 날을 그리고 있으므로 **그 칸이 스스로 말해야** 한다.
   * ★ 주간·월간 둘 다 이 한 칸(`DayCell`)을 쓰므로 **한 번 고치면 둘 다** 선다.
   */
  const anom = day?.needsClose === true;
  const cls = ['wkp__day', picked ? 'is-sel' : '', k === todayKey ? 'is-today' : '',
    dow === 0 ? 'is-sun' : dow === 6 ? 'is-sat' : '', day ? '' : 'is-off',
    anom ? 'is-anom' : ''].filter(Boolean).join(' ');

  const body = (
    <>
      <span className="wkp__d">
        <span className="wkp__w">{WEEKDAY[dow]}</span>
        <span className="wkp__n">{d.getUTCDate()}</span>
      </span>
      {word ? <span className="wkp__total is-none">{word}</span> : <span className="wkp__total">{spanText(total)}</span>}
      {day?.checkIn && <span className="wkp__t"><b>출</b>{day.checkIn}</span>}
      {day?.checkOut && <span className="wkp__t"><b>퇴</b>{day.checkOut}</span>}
      {day && day.overtimeMinutes > 0 && <span className="wkp__ot">초과 {spanText(day.overtimeMinutes)}</span>}
      {anom && <span className="wkp__anomtag">마감 이상</span>}
    </>
  );
  if (!day) return <span className={cls}>{body}</span>;
  return (
    <button type="button" className={cls} aria-pressed={picked} onClick={() => onPick(k)}>{body}</button>
  );
}

function MonthGrid({ keys, byDate, picked, onPick, todayKey, restSet }: {
  keys: string[]; byDate: ReadonlyMap<string, DailyWorkDto>; picked: string | null;
  onPick: (k: string) => void; todayKey: string;
  /** 그 달의 쉬는 날 — 서버가 준다(A-3). */
  restSet: ReadonlySet<string> | null;
}) {
  // 월요일이 첫 칸 — 주간과 같은 자를 쓴다.
  const lead = (fromKey(keys[0]!).getUTCDay() + 6) % 7;
  return (
    <div className="wkp__month">
      <div className="wkp__mhd" aria-hidden="true">
        {['월', '화', '수', '목', '금', '토', '일'].map((w) => <span key={w}>{w}</span>)}
      </div>
      <div className="wkp__mgrid" role="group" aria-label="이번 달">
        {Array.from({ length: lead }, (_, i) => <span key={`x${i}`} className="wkp__day is-off is-blank" />)}
        {keys.map((k) => <DayCell key={k} k={k} day={byDate.get(k) ?? null} picked={picked === k} onPick={onPick} todayKey={todayKey} restSet={restSet} />)}
      </div>
    </div>
  );
}

/** 고른 날 — 한 줄 · 24시간 띠 · 그날 기록 · 범례. */
function DayDetail({ day, requests, requestsKnown, inPop, onCloseDay, closingDate, closeError }: {
  day: DailyWorkDto | null;
  requests?: Array<{ label: string; status: string }>;
  /**
   * 낸 신청을 **읽어 냈는가** (2026-09-06). 이 칸이 없으면 「그날 신청 없음」과
   * 「아직 못 읽었다」가 같은 모양이 된다 — 「없음」이라 적어 놓고 사실은 모르는 것이다.
   */
  requestsKnown?: boolean;
  inPop?: boolean;
  /**
   * 마감 — **표시는 달력 칸이, 조치는 여기서** 한다 (2026-09-08 사용자 지시).
   * ★ 달력이 「마감 이상」이라고 말하고, 그 날을 열면 여기서 고친다. 단추를 칸에 두면
   *   달력이 «누르는 판»이 되어 날짜를 고르는 일과 섞인다.
   */
  onCloseDay?: (date: string) => void;
  closingDate?: string | null;
  closeError?: string | null;
}) {
  const segs = useMemo(() => (day ? daySegments(day) : []), [day]);
  /**
   * 근무 상태내역 — 서버가 이미 **시각 오름차순**으로 준다(옆 띠와 같은 방향).
   * ★ 옛 서버는 이 칸을 안 준다 — 그때는 사실 기록에서 두 줄을 만든다(종전과 같은 화면).
   */
  const lines = useMemo(() => {
    if (!day) return [];
    const entries = day.statusLog ?? fallbackLogFrom(day);
    return toWorkLogLines(entries, { baseStart: day.baseStart, late: day.late });
  }, [day]);
  if (!day) {
    return <div className={`wkp__detail${inPop ? ' is-pop' : ''}`}><p className="wkp__note">그날은 기록이 없습니다.</p></div>;
  }
  const total = day.creditedMinutes + day.overtimeMinutes;
  const kinds = new Set(segs.map((s) => s.kind));

  return (
    <div className={`wkp__detail${inPop ? ' is-pop' : ''}`}>
      {/* ── 한 줄 ── */}
      <div className="wkp__strip">
        <span className="wkp__ds"><b>근무 시작</b><span className="wkp__num">{day.checkIn ?? '—'}</span></span>
        <span className="wkp__ds"><b>근무 종료</b><span className="wkp__num">{day.checkOut ?? '—'}</span></span>
        <span className="wkp__ds"><b>총 근로시간</b><span className="wkp__num">{spanText(total)}</span></span>
        <span className="wkp__ds"><b>휴게</b><span className="wkp__num">{formatWorkMinutes(day.breakMinutes)}</span></span>
        <span className="wkp__sep" aria-hidden />
        <span className="wkp__ds">
          <b>상세 근로시간</b>
          업무 {spanText(day.creditedMinutes)} · 승인 초과 {spanText(day.overtimeMinutes)}
          {day.unclaimedMinutes > 0 ? ` · 미포함 ${spanText(day.unclaimedMinutes)}` : ''}
        </span>
        <span className="wkp__ds">
          <b>승인요청</b>
          {requests && requests.length > 0
            ? requests.map((r) => <span key={r.label} className="wkp__badge">{r.label} {r.status}</span>)
            /* ★ 못 읽었으면 「없음」이 아니라 「모름」이다 — 안 가르면 화면이 거짓말을 한다. */
            : requestsKnown
              ? <span className="wkp__dim">없음</span>
              : <span className="wkp__dim" title="낸 신청을 읽지 못했습니다">모름</span>}
        </span>
      </div>

      {/* ── 띠 + 그날 기록 ── */}
      <div className="wkp__graph">
        <div className="wkp__tl">
          <div className="wkp__track">
            <span className="wkp__grid" aria-hidden>
              <i style={{ left: '25%' }} /><i style={{ left: '50%' }} /><i style={{ left: '75%' }} />
            </span>
            {segs.filter((s) => s.kind !== 'night').map((s, i) => (
              <span key={i} className={`wkp__seg is-${s.kind}`} style={{ left: pct(s.from), width: pct(s.to - s.from) }} />
            ))}
            {/* ★ 야간은 «겹치는 값» 이라 제 구간을 갖지 않고 그 시간의 **밑단**에 깔린다. */}
            {segs.filter((s) => s.kind === 'night').map((s, i) => (
              <span key={`n${i}`} className="wkp__seg is-night" style={{ left: pct(s.from), width: pct(s.to - s.from) }} />
            ))}
          </div>
          <div className="wkp__ax" aria-hidden="true">
            {Array.from({ length: 25 }, (_, i) => (
              <span key={i} className={i % 6 === 0 ? 'is-q' : ''} style={{ left: `${(i / 24) * 100}%` }}>
                {String(i).padStart(2, '0')}
              </span>
            ))}
          </div>
        </div>

        <span className="wkp__grule" aria-hidden />

        <div className="wkp__log">
          {/*
            ★★ 제목이 **«몇 건인지»** 를 적는다 — 아래 창은 세 줄만 보이고 굴린다. 안 적으면
               긴 하루도 「세 건뿐」인 것처럼 읽힌다(감춰진 양을 밝히는 것이 스크롤의 값이다).
          */}
          <div className="wkp__logt">
            {tLayout('layout.worklog.title')}{' '}
            <span className="wkp__dim">{tLayout('layout.worklog.count', { n: lines.length })}</span>
          </div>
          <div
            className="wkp__logs"
            tabIndex={0}
            role="group"
            aria-label={tLayout('layout.worklog.title')}
          >
            {lines.map((l, i) => (
              <div className="wkp__line" key={`${l.at}-${l.label}-${i}`}>
                <time>{l.at}</time>
                <span className="wkp__linem">
                  {l.label}
                  {l.note && <span className="wkp__badge is-bad">{l.note}</span>}
                </span>
                <span className={`wkp__badge is-${l.tone}`}>{l.badge}</span>
              </div>
            ))}
            {lines.length === 0 && <p className="wkp__note">{tLayout('layout.worklog.empty')}</p>}
          </div>
        </div>
      </div>

      {/* ── 시간 구성 — 띠와 기록 «아래» 를 가로지른다 ── */}
      <div className="wkp__foot">
        <div className="wkp__lg">
          <span className="wkp__lgt">시간 구성</span>
          {(['work', 'un', 'ot', 'night', 'leave'] as const)
            .filter((k) => kinds.has(k))
            .map((k) => <span key={k}><i className={`is-${k}`} />{SEG_LABEL[k]}</span>)}
          {kinds.size === 0 && <span className="wkp__dim">그릴 구간이 없습니다</span>}
        </div>
        {/* ★ 띠의 «읽는 법»(겹치지 않는다 · 야간은 밑단 · 휴게는 안 그린다)은 걷어냈다
            (2026-09-08 사용자 지시) — 매번 읽는 문장이 아니다(정본 P8). */}
        {day.needsClose && (
          <div className="wkp__close">
            <span className="wkp__closet">
              <AlertCircle size={13} aria-hidden />
              출근만 있고 퇴근이 없습니다 · 소정 종료 {day.baseEnd}
            </span>
            {onCloseDay && (
              <button
                type="button"
                className="wkp__closebtn"
                onClick={() => onCloseDay(day.date)}
                disabled={closingDate !== null && closingDate !== undefined}
              >
                {closingDate === day.date ? '마감 중…' : `${day.baseEnd} 으로 마감`}
              </button>
            )}
            {closeError && <span className="wkp__closeerr" role="alert">{closeError}</span>}
            {/* ★ 「서버가 몰래 닫지 않는다」는 남긴다 — 지어낸 값인 줄 모르게 되면 안 된다. */}
            <span className="wkp__closenote">
              마감하면 퇴근이 <b>소정 종료 시각</b>으로 남습니다 — 실제로 나간 시각이 아니라는 사실도 함께 기록됩니다.
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

